import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, Form, Button, Alert, Spinner, Row, Col, Badge, Modal, Table, Card,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FaPlus, FaCheck, FaEdit, FaTrash, FaExternalLinkAlt, FaBox, FaShoppingCart, FaEuroSign,
} from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { api } from '../../../contexts/AuthContext';

const ACCENT = '#60A5FA';

const CATEGORY_CHOICES = [
  { value: 'travel', label: 'Travel & Experiences' },
  { value: 'electronics', label: 'Electronics & Gadgets' },
  { value: 'clothing', label: 'Fashion & Accessories' },
  { value: 'education', label: 'Education & Courses' },
  { value: 'home', label: 'Home & Furniture' },
  { value: 'books', label: 'Books & Learning' },
  { value: 'charity', label: 'Charity & Donations' },
  { value: 'events', label: 'Events & Concerts' },
  { value: 'country_house', label: 'Country House' },
  { value: 'car', label: 'Car' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: 25, label: 'Low' },
  { value: 50, label: 'Medium' },
  { value: 80, label: 'High' },
];

const emptyForm = () => ({
  name: '',
  description: '',
  url: '',
  est_cost: '',
  category: 'other',
  est_date: null,
  priority: 50,
  is_purchased: false,
});

const toDateKey = (value) => {
  if (!value) return null;
  return moment(value).format('YYYY-MM-DD');
};

const parseDate = (key) => {
  if (!key) return null;
  const [y, m, d] = String(key).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const priorityColor = (priority) => {
  if (priority >= 67) return '#f87171';
  if (priority >= 34) return '#fbbf24';
  return '#60A5FA';
};

const priorityLabel = (priority) => {
  if (priority >= 67) return 'High';
  if (priority >= 34) return 'Medium';
  return 'Low';
};

const ItemModal = ({ show, onHide, onSubmit, initialData }) => {
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setForm({
        name: initialData.name || '',
        description: initialData.description || '',
        url: initialData.url || '',
        est_cost: initialData.est_cost != null ? String(initialData.est_cost) : '',
        category: initialData.category || 'other',
        est_date: parseDate(initialData.est_date),
        priority: initialData.priority >= 67 ? 80 : initialData.priority >= 34 ? 50 : 25,
        is_purchased: Boolean(initialData.is_purchased),
      });
    } else {
      setForm(emptyForm());
    }
  }, [show, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim() || null,
      url: form.url.trim() || '',
      est_cost: form.est_cost === '' ? null : Number(form.est_cost),
      category: form.category,
      est_date: toDateKey(form.est_date),
      priority: form.priority,
      is_purchased: form.is_purchased,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
        <Modal.Title>{initialData ? 'Edit wish' : 'Add to wishlist'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>What do you want?</Form.Label>
            <Form.Control
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. MacBook, weekend in Crete"
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Link (optional)</Form.Label>
            <Form.Control
              type="text"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://..."
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Estimated cost (€)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.est_cost}
                  onChange={(e) => setForm({ ...form, est_cost: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  {CATEGORY_CHOICES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <div className="d-flex gap-2">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      type="button"
                      size="sm"
                      className="flex-grow-1"
                      variant={form.priority === opt.value ? 'primary' : 'outline-secondary'}
                      style={
                        form.priority === opt.value
                          ? { backgroundColor: priorityColor(opt.value), borderColor: priorityColor(opt.value) }
                          : undefined
                      }
                      onClick={() => setForm({ ...form, priority: opt.value })}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ display: 'block' }}>Target date</Form.Label>
                <DatePicker
                  selected={form.est_date}
                  onChange={(date) => setForm({ ...form, est_date: date })}
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  placeholderText="Optional"
                  className="form-control bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          {initialData && (
            <Form.Check
              type="switch"
              id="wish-purchased"
              label="Already bought"
              checked={form.is_purchased}
              onChange={(e) => setForm({ ...form, is_purchased: e.target.checked })}
              className="mb-1"
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #333' }}>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button type="submit" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
            {initialData ? 'Save' : 'Add'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const Wishlist = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open'); // open | bought | all | overdue
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortKey, setSortKey] = useState('priority');

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/wishlists/');
      setItems(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load wishlist.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const stats = useMemo(() => {
    const open = items.filter((i) => !i.is_purchased);
    const bought = items.filter((i) => i.is_purchased);
    const overdue = open.filter((i) => i.is_overdue || (i.est_date && moment(i.est_date).isBefore(moment(), 'day')));
    const openCost = open.reduce((s, i) => s + (Number(i.est_cost) || 0), 0);
    const boughtCost = bought.reduce((s, i) => s + (Number(i.est_cost) || 0), 0);

    const byCategory = {};
    open.forEach((i) => {
      const key = i.category || 'other';
      byCategory[key] = (byCategory[key] || 0) + (Number(i.est_cost) || 0);
    });

    return {
      openCount: open.length,
      boughtCount: bought.length,
      overdueCount: overdue.length,
      openCost,
      boughtCost,
      byCategory,
    };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = [...items];

    if (statusFilter === 'open') list = list.filter((i) => !i.is_purchased);
    if (statusFilter === 'bought') list = list.filter((i) => i.is_purchased);
    if (statusFilter === 'overdue') {
      list = list.filter(
        (i) =>
          !i.is_purchased &&
          (i.is_overdue || (i.est_date && moment(i.est_date).isBefore(moment(), 'day')))
      );
    }
    if (categoryFilter) list = list.filter((i) => i.category === categoryFilter);
    if (q) {
      list = list.filter(
        (i) =>
          (i.name || '').toLowerCase().includes(q) ||
          (i.description || '').toLowerCase().includes(q) ||
          (i.category_label || '').toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      if (sortKey === 'priority') return (b.priority || 0) - (a.priority || 0);
      if (sortKey === 'cost') return (Number(b.est_cost) || 0) - (Number(a.est_cost) || 0);
      if (sortKey === 'date') {
        if (!a.est_date) return 1;
        if (!b.est_date) return -1;
        return moment(a.est_date).valueOf() - moment(b.est_date).valueOf();
      }
      return (a.name || '').localeCompare(b.name || '');
    });

    return list;
  }, [items, search, statusFilter, categoryFilter, sortKey]);

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleSave = async (data) => {
    try {
      if (editing) {
        await api.put(`/api/wishlists/${editing.id}/`, data);
        toast.success('Updated');
      } else {
        await api.post('/api/wishlists/', data);
        toast.success('Added to wishlist');
      }
      closeModal();
      await fetchItems();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save');
    }
  };

  const toggleBought = async (item) => {
    try {
      await api.patch(`/api/wishlists/${item.id}/`, { is_purchased: !item.is_purchased });
      toast.success(item.is_purchased ? 'Moved back to wishlist' : 'Marked as bought');
      await fetchItems();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete “${item.name}”?`)) return;
    try {
      await api.delete(`/api/wishlists/${item.id}/`);
      toast.success('Deleted');
      await fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const categoryBreakdown = Object.entries(stats.byCategory)
    .filter(([, cost]) => cost > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 style={{ color: ACCENT, marginBottom: 4 }}>Wishlist</h2>
          <div style={{ color: '#9ca3af', fontSize: 13 }}>
            Things you want · what they cost · what you already bought
          </div>
        </div>
        <Button
          style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
        >
          <FaPlus className="me-2" /> Add wish
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <Row className="mb-3 g-3">
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#9ca3af', fontSize: 13 }}>
                    <FaBox style={{ color: ACCENT }} /> Still want
                  </div>
                  <h3 className="mb-0">{stats.openCount}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#9ca3af', fontSize: 13 }}>
                    <FaEuroSign style={{ color: '#fbbf24' }} /> To buy
                  </div>
                  <h3 className="mb-0" style={{ color: '#fbbf24' }}>€{stats.openCost.toFixed(2)}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#9ca3af', fontSize: 13 }}>
                    <FaShoppingCart style={{ color: '#22c55e' }} /> Bought
                  </div>
                  <h3 className="mb-0 text-success">
                    {stats.boughtCount} · €{stats.boughtCost.toFixed(2)}
                  </h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="bg-dark text-light border-secondary h-100">
                <Card.Body>
                  <div style={{ color: '#9ca3af', fontSize: 13 }} className="mb-1">Overdue targets</div>
                  <h3 className="mb-0" style={{ color: stats.overdueCount ? '#f87171' : '#fff' }}>
                    {stats.overdueCount}
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {categoryBreakdown.length > 0 && (
            <Card className="bg-dark text-light border-secondary mb-3">
              <Card.Body className="py-3">
                <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 8 }}>
                  Open wishlist by category
                </div>
                <div className="d-flex flex-wrap gap-3">
                  {categoryBreakdown.map(([cat, cost]) => (
                    <div key={cat}>
                      <span style={{ color: '#d1d5db' }}>
                        {CATEGORY_CHOICES.find((c) => c.value === cat)?.label || cat}
                      </span>
                      <span className="ms-2" style={{ color: ACCENT, fontWeight: 600 }}>
                        €{cost.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          <Card className="bg-dark text-light border-secondary">
            <Card.Header className="bg-dark border-secondary d-flex flex-wrap gap-2 align-items-center">
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-dark text-light border-secondary"
                style={{ width: 'auto' }}
              >
                <option value="open">Wanted</option>
                <option value="bought">Bought</option>
                <option value="overdue">Overdue</option>
                <option value="all">All</option>
              </Form.Select>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-dark text-light border-secondary"
                style={{ width: 'auto', maxWidth: 220 }}
              >
                <option value="">All categories</option>
                {CATEGORY_CHOICES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </Form.Select>
              <Form.Select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="bg-dark text-light border-secondary"
                style={{ width: 'auto' }}
              >
                <option value="priority">Sort: priority</option>
                <option value="cost">Sort: cost</option>
                <option value="date">Sort: target date</option>
                <option value="name">Sort: name</option>
              </Form.Select>
              <Form.Control
                type="search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-dark text-light border-secondary"
                style={{ width: 220 }}
              />
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover variant="dark" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Cost</th>
                    <th>Target</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4" style={{ color: '#9ca3af' }}>
                        Nothing here. Add something you want to buy or save for.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((item) => {
                      const overdue =
                        !item.is_purchased &&
                        (item.is_overdue ||
                          (item.est_date && moment(item.est_date).isBefore(moment(), 'day')));
                      return (
                        <tr
                          key={item.id}
                          style={{ opacity: item.is_purchased ? 0.65 : 1 }}
                        >
                          <td>
                            <div
                              style={{
                                fontWeight: 600,
                                textDecoration: item.is_purchased ? 'line-through' : 'none',
                              }}
                            >
                              {item.name}
                              {item.url ? (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ms-2"
                                  style={{ color: ACCENT }}
                                  title="Open link"
                                >
                                  <FaExternalLinkAlt size={12} />
                                </a>
                              ) : null}
                            </div>
                            {item.description ? (
                              <small style={{ color: '#9ca3af' }}>{item.description}</small>
                            ) : null}
                            {item.is_purchased && (
                              <div>
                                <Badge bg="success" className="mt-1">Bought</Badge>
                              </div>
                            )}
                          </td>
                          <td style={{ color: '#d1d5db' }}>
                            {item.category_label ||
                              CATEGORY_CHOICES.find((c) => c.value === item.category)?.label ||
                              item.category}
                          </td>
                          <td>
                            <Badge
                              style={{
                                backgroundColor: priorityColor(item.priority),
                                color: '#111',
                              }}
                            >
                              {item.priority_label || priorityLabel(item.priority)}
                            </Badge>
                          </td>
                          <td style={{ fontWeight: 600 }}>
                            {item.est_cost != null ? `€${Number(item.est_cost).toFixed(2)}` : '—'}
                          </td>
                          <td style={{ color: overdue ? '#f87171' : '#d1d5db' }}>
                            {item.est_date
                              ? moment(item.est_date).format('DD-MM-YYYY')
                              : '—'}
                            {overdue ? (
                              <Badge bg="danger" className="ms-2">Overdue</Badge>
                            ) : null}
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end flex-wrap">
                              <Button
                                size="sm"
                                variant={item.is_purchased ? 'success' : 'outline-success'}
                                title={item.is_purchased ? 'Mark as wanted again' : 'Mark bought'}
                                onClick={() => toggleBought(item)}
                              >
                                <FaCheck />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                style={{ borderColor: ACCENT, color: ACCENT }}
                                onClick={() => {
                                  setEditing(item);
                                  setShowModal(true);
                                }}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDelete(item)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}

      <ItemModal
        show={showModal}
        onHide={closeModal}
        onSubmit={handleSave}
        initialData={editing}
      />
    </Container>
  );
};

export default Wishlist;
